// PR-28: Thin wrapper around the native IndexedDB API for the mobile shell.
//
// Why no `idb` library: a single use case (read/write a few stores) doesn't
// justify a dependency. Worth ~80 LOC of plumbing for the build savings.
//
// Safari private mode + some embedded browsers throw on `indexedDB.open` —
// we catch and degrade to an in-memory Map so the app still runs (just
// without persistence). The fallback is silent on purpose; offline persistence
// is a nice-to-have, not load-bearing for the in-session capture queue.

const DB_NAME = 'draftlize-mobile'
const DB_VERSION = 1

export interface ObjectStoreDef {
  name: string
  keyPath?: string
  indexes?: Array<{ name: string; keyPath: string; unique?: boolean }>
}

const STORES: ObjectStoreDef[] = [
  { name: 'cards_by_project', keyPath: 'project_id' },
  { name: 'projects_list', keyPath: 'cache_key' }, // single key 'all'
  {
    name: 'capture_queue',
    keyPath: 'id',
    indexes: [{ name: 'created_at', keyPath: 'created_at' }],
  },
]

// In-memory fallback for browsers where IndexedDB throws (Safari private,
// certain in-app browsers). Per-store map keyed by the store's keyPath value.
const fallback: Map<string, Map<string, unknown>> = new Map(
  STORES.map((s) => [s.name, new Map()]),
)
let useFallback = false

let dbPromise: Promise<IDBDatabase | null> | null = null

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve) => {
    if (typeof indexedDB === 'undefined') {
      useFallback = true
      resolve(null)
      return
    }
    let req: IDBOpenDBRequest
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION)
    } catch {
      useFallback = true
      resolve(null)
      return
    }
    req.onupgradeneeded = () => {
      const db = req.result
      for (const def of STORES) {
        if (!db.objectStoreNames.contains(def.name)) {
          const os = db.createObjectStore(def.name, { keyPath: def.keyPath })
          for (const idx of def.indexes ?? []) {
            os.createIndex(idx.name, idx.keyPath, { unique: idx.unique ?? false })
          }
        }
      }
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => {
      useFallback = true
      resolve(null)
    }
    req.onblocked = () => {
      useFallback = true
      resolve(null)
    }
  })
  return dbPromise
}

function tx(db: IDBDatabase, store: string, mode: IDBTransactionMode): IDBObjectStore {
  return db.transaction(store, mode).objectStore(store)
}

export async function getValue<T>(store: string, key: string): Promise<T | undefined> {
  const db = await openDb()
  if (!db || useFallback) {
    return fallback.get(store)?.get(key) as T | undefined
  }
  return new Promise((resolve) => {
    try {
      const req = tx(db, store, 'readonly').get(key)
      req.onsuccess = () => resolve(req.result as T | undefined)
      req.onerror = () => resolve(undefined)
    } catch {
      resolve(undefined)
    }
  })
}

export async function putValue(store: string, value: unknown): Promise<void> {
  const db = await openDb()
  // Determine the key from the store def's keyPath so we can also write
  // to the in-memory fallback.
  const def = STORES.find((s) => s.name === store)
  const keyPath = def?.keyPath ?? 'id'
  const key = String((value as Record<string, unknown>)[keyPath] ?? '')
  if (!db || useFallback) {
    fallback.get(store)?.set(key, value)
    return
  }
  return new Promise((resolve) => {
    try {
      const req = tx(db, store, 'readwrite').put(value)
      req.onsuccess = () => resolve()
      req.onerror = () => resolve()
    } catch {
      // Quota or write error — best-effort cache. Resolve so callers don't
      // block on a non-critical persistence failure.
      resolve()
    }
  })
}

export async function deleteValue(store: string, key: string): Promise<void> {
  const db = await openDb()
  if (!db || useFallback) {
    fallback.get(store)?.delete(key)
    return
  }
  return new Promise((resolve) => {
    try {
      const req = tx(db, store, 'readwrite').delete(key)
      req.onsuccess = () => resolve()
      req.onerror = () => resolve()
    } catch {
      resolve()
    }
  })
}

export async function getAll<T>(store: string): Promise<T[]> {
  const db = await openDb()
  if (!db || useFallback) {
    return Array.from(fallback.get(store)?.values() ?? []) as T[]
  }
  return new Promise((resolve) => {
    try {
      const req = tx(db, store, 'readonly').getAll()
      req.onsuccess = () => resolve((req.result ?? []) as T[])
      req.onerror = () => resolve([])
    } catch {
      resolve([])
    }
  })
}
